import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { Autocomplete, Stack, TextField, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";

import {
  buildPrivateSelectionErrorMessage,
  CHIP_VARIANTS,
  getOptionLabel,
  isOptionEqualToValue,
  renderAllPartnersOption,
  renderChip,
  renderEmailOption,
  renderGroupOption,
  renderNoPartnersAlert,
  renderPartnerOption,
  renderPendingInvitesOnlyAlert,
  renderStringOption,
  THEME_COLORS,
  TYPOGRAPHY_VARIANTS,
} from "./utils";

import type { NetworkMember, PartnerSelectionItem } from "../../types";
import type { ShipmentVisibility } from "../VisibilityRadioGroup/VisibilityRadioGroup";

export interface UnifiedPartnerSelectorRef {
  getInputValue: () => string;
  getLastTypedValue: () => string;
}

interface UnifiedPartnerSelectorProps {
  selectedItems: PartnerSelectionItem[];
  options: PartnerSelectionItem[];
  inputValue: string;
  lastTypedValue: string;
  onSelectionChange: (newSelections: (string | PartnerSelectionItem)[]) => void;
  onInputChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onBlur: (event: React.FocusEvent) => void;
  onPaste: (event: React.ClipboardEvent) => void;
  confirmedPartners: NetworkMember[];
  pendingInviteCount: number;
  shouldShowEmptyNetworkAlert: boolean;
  hasAllPartnersSelected: boolean;
  loadingPartners?: boolean;
  loadingGroups?: boolean;
  loadingPendingInvites?: boolean;
  partnersError?: string | null;
  groupsError?: string | null;
  emailError?: string | null;
  shipmentVisibility: ShipmentVisibility;
}

export const UnifiedPartnerSelector = forwardRef<
  UnifiedPartnerSelectorRef,
  UnifiedPartnerSelectorProps
>((props, ref) => {
  const {
    selectedItems,
    options,
    inputValue,
    lastTypedValue,
    onSelectionChange,
    onInputChange,
    onKeyDown,
    onBlur,
    onPaste,
    confirmedPartners,
    pendingInviteCount,
    shouldShowEmptyNetworkAlert,
    hasAllPartnersSelected,
    loadingPartners = false,
    loadingGroups = false,
    loadingPendingInvites = false,
    partnersError = null,
    groupsError = null,
    emailError = null,
    shipmentVisibility,
  } = props;

  const currentLastTypedValue = useRef(lastTypedValue);

  // Keep the ref updated with the latest prop value
  useEffect(() => {
    currentLastTypedValue.current = lastTypedValue;
  }, [lastTypedValue]);

  useImperativeHandle(
    ref,
    () => ({
      getInputValue: () => inputValue,
      getLastTypedValue: () => currentLastTypedValue.current,
    }),
    [inputValue],
  );

  return (
    <LoadingWrappedNode
      loading={loadingPartners || loadingGroups || loadingPendingInvites}
    >
      <Stack spacing={1}>
        <Autocomplete<PartnerSelectionItem | string, true, false, true>
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "wrap",
              "& .MuiAutocomplete-input": {
                flexBasis: selectedItems.length > 0 ? "100%" : "auto",
                minWidth: selectedItems.length > 0 ? "100%" : "30px",
              },
            },
          }}
          multiple
          value={selectedItems}
          onChange={(_, newValue) => onSelectionChange(newValue)}
          options={options}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          onInputChange={(_, value) => onInputChange(value)}
          filterSelectedOptions
          freeSolo
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={(optionProps, option) => {
            if (typeof option === "string") {
              return renderStringOption(optionProps, option);
            }

            if (option.type === "all_partners") {
              return renderAllPartnersOption(optionProps, option);
            }

            if (option.type === "partner") {
              return renderPartnerOption(
                optionProps,
                option,
                confirmedPartners,
              );
            }

            if (option.type === "group") {
              return renderGroupOption(optionProps, option);
            }

            return renderEmailOption(optionProps, option);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => renderChip(option, index, getTagProps))
          }
          renderInput={(params) => {
            const handleKeyDownWithStopPropagation = (
              event: React.KeyboardEvent,
            ) => {
              onKeyDown(event);
              // Prevent MUI Autocomplete from clearing invalid emails
              if (
                (event.key === "Enter" || event.key === " ") &&
                inputValue.trim()
              ) {
                event.stopPropagation();
              }
            };

            return (
              <TextField
                {...params}
                placeholder={
                  selectedItems.length === 0
                    ? "Type name, email, or select all..."
                    : ""
                }
                variant={CHIP_VARIANTS.OUTLINED}
                onKeyDown={handleKeyDownWithStopPropagation}
                onBlur={onBlur}
                onPaste={onPaste}
                error={!!emailError}
                helperText={emailError}
                multiline
                maxRows={4}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    "& .MuiInputBase-input": {
                      whiteSpace: "pre-wrap",
                      wordBreak: "normal",
                      overflowWrap: "anywhere",
                      lineBreak: "anywhere",
                    },
                  },
                }}
              />
            );
          }}
        />

        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          color={THEME_COLORS.TEXT_SECONDARY}
        >
          Evotrux will notify all relevant carriers plus any partners you
          indicate above.
        </Typography>

        {shouldShowEmptyNetworkAlert &&
          (pendingInviteCount > 0
            ? renderPendingInvitesOnlyAlert(pendingInviteCount)
            : renderNoPartnersAlert())}

        {!hasAllPartnersSelected &&
          selectedItems.length === 0 &&
          shipmentVisibility === "PRIVATE" && (
            <ErrorTypography
              error={buildPrivateSelectionErrorMessage({
                hasConfirmedPartners: confirmedPartners.length > 0,
                hasPendingInvites: pendingInviteCount > 0,
              })}
            />
          )}

        {partnersError && <ErrorTypography error={partnersError} />}
        {groupsError && <ErrorTypography error={groupsError} />}
      </Stack>
    </LoadingWrappedNode>
  );
});
